import { useState } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import type { ScopedSecret } from '@reflex/shared';
import { formatDateOnly } from '@reflex/ui/lib/format';
import { EmptyState } from '@reflex/ui/components/shared/EmptyState';
import { SectionCard } from '@reflex/ui/components/shared/SectionCard';
import { Button } from '@reflex/ui/components/ui/button';
import { AddSecretPopover } from './AddSecretPopover';
import { SecretInputDialog } from '@/components/forms/SecretInputDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@reflex/ui/components/ui/alert-dialog';

/**
 * Generic CRUD surface for ID-first scoped secrets (user-tier and
 * team-tier). The component is intentionally read-as-list-only —
 * values are write-only after creation, matching the org-tier
 * `CustomSecretsSection` pattern.
 *
 * Layout uses {@link SectionCard} so personal, team, and org custom
 * secrets share the same section vocabulary as model-provider keys.
 *
 * Each row renders the human-readable `name` plus a Pencil button that
 * pops the value-only `SecretInputDialog` (the secret id stays the
 * stable identity behind the scenes) and a Trash button that opens a
 * confirm dialog. Add a new secret via the inline `AddSecretPopover`.
 *
 * The component is deliberately presentational: the host page provides
 * the data + the three mutation callbacks; this component owns only the
 * open/edit dialog state.
 */
interface ScopedSecretsManagerProps {
  /** Heading rendered above the list (e.g. `Team Secrets`). */
  title: string;
  /** Optional helper text under the heading. */
  description?: string;
  /** Loaded secrets list. Caller is responsible for fetching. */
  secrets: ScopedSecret[];
  isLoading?: boolean;
  isPending?: boolean;
  /** Optional read-only flag: hides Add/Edit/Delete affordances. */
  readOnly?: boolean;
  onCreate: (name: string, value: string) => void;
  onUpdate: (secretId: string, value: string) => void | Promise<void>;
  onDelete: (secretId: string) => void;
  /** Empty-state copy displayed when there are no secrets. */
  emptyState?: React.ReactNode;
  /** Optional per-secret error message shown in the edit dialog. */
  editErrorMessage?: (secretId: string) => string | null | undefined;
  /** Fired when the edit dialog opens or closes. */
  onEditDialogOpenChange?: (open: boolean) => void;
  /**
   * When false, hide the section-header Add control. Use on pages that put
   * the page-level create CTA in `PageHeader` instead.
   */
  showSectionAction?: boolean;
}

export function ScopedSecretsManager({
  title,
  description,
  secrets,
  isLoading = false,
  isPending = false,
  readOnly = false,
  onCreate,
  onUpdate,
  onDelete,
  emptyState,
  editErrorMessage,
  onEditDialogOpenChange,
  showSectionAction = true,
}: ScopedSecretsManagerProps) {
  const [editing, setEditing] = useState<ScopedSecret | null>(null);
  const [deleting, setDeleting] = useState<ScopedSecret | null>(null);

  const emptyContent =
    typeof emptyState === 'string' || emptyState == null ? (
      <EmptyState title={typeof emptyState === 'string' ? emptyState : 'No secrets configured.'} />
    ) : (
      emptyState
    );

  return (
    <>
      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading secrets…</p>
      ) : (
        <SectionCard
          title={title}
          description={description}
          action={
            !readOnly && showSectionAction ? (
              <AddSecretPopover
                onSubmit={(name, value) => onCreate(name, value)}
                isPending={isPending}
                disabled={isPending}
              />
            ) : undefined
          }
          contentClassName={secrets.length > 0 ? 'divide-y py-0' : 'py-0'}
          className="gap-2 py-4 sm:gap-3"
          data-testid="scoped-secrets-manager"
        >
          {secrets.length > 0
            ? secrets.map((secret) => (
                <div
                  key={secret.id}
                  className="flex items-center justify-between gap-3 py-2"
                  data-testid={`scoped-secret-row-${secret.id}`}
                >
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5 sm:flex-row sm:items-baseline sm:gap-2">
                    <span className="font-mono text-sm">{secret.name}</span>
                    <span className="text-xs text-muted-foreground">
                      updated {formatDateOnly(secret.updatedAt)}
                    </span>
                  </div>
                  {!readOnly && (
                    <div className="flex shrink-0 items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditing(secret)}
                        disabled={isPending}
                        aria-label={`Edit ${secret.name}`}
                      >
                        <Pencil className="size-3" /> Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDeleting(secret)}
                        disabled={isPending}
                        aria-label={`Delete ${secret.name}`}
                      >
                        <Trash2 className="size-3" />
                      </Button>
                    </div>
                  )}
                </div>
              ))
            : emptyContent}
        </SectionCard>
      )}

      {editing && (
        <SecretInputDialog
          open={true}
          onOpenChange={(next) => {
            onEditDialogOpenChange?.(next);
            if (!next) setEditing(null);
          }}
          secretKey={editing.name}
          label={editing.name}
          isUpdate
          errorMessage={editErrorMessage?.(editing.id) ?? null}
          onSubmit={(value) => {
            void Promise.resolve(onUpdate(editing.id, value))
              .then(() => setEditing(null))
              .catch(() => {
                /* keep dialog open so editErrorMessage can surface */
              });
          }}
          isPending={isPending}
        />
      )}

      <AlertDialog
        open={deleting !== null}
        onOpenChange={(next) => {
          if (!next) setDeleting(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this secret?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleting?.name} will be permanently removed. Any agent that references this secret by
              its id will fail to mount it on next launch.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (deleting) onDelete(deleting.id);
                setDeleting(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
